import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ReactComponent as MarketClose } from '../../../assets/icons/main/marketClose.svg'
import { ReactComponent as MarketSearch } from '../../../assets/icons/main/MarketSearch.svg'
import './Market.css';

const NEW_API_BASE = 'https://api-ru.fynex.cc/charts';
const WS_URL = 'wss://api-ru.fynex.cc/ws/prices';

const CRYPTO_SYMBOLS = {
    usdt: ["BTCUSDT", "ETHUSDT", "BNBUSDT", "LTCUSDT", "ADAUSDT", "XRPUSDT", "XLMUSDT", "TRXUSDT", "ETCUSDT", "VETUSDT", "LINKUSDT", "ATOMUSDT", "ALGOUSDT", "DOGEUSDT", "HBARUSDT", "BCHUSDT", "SOLUSDT", "DOTUSDT", "EGLDUSDT", "UNIUSDT", "AVAXUSDT", "AAVEUSDT", "NEARUSDT", "FILUSDT", "AXSUSDT", "GRTUSDT", "TWTUSDT", "SHIBUSDT", "ICPUSDT", "QNTUSDT", "IMXUSDT", "LDOUSDT", "OPUSDT", "APTUSDT", "ARBUSDT", "WBTCUSDT"],
    btc: ["DOGEBTC","BCHBTC","ATOMBTC","LDOBTC","ADABTC","WBTCBTC","APTBTC","AAVEBTC","ETHBTC","SOLBTC","AVAXBTC","ARBBTC","VETBTC","TRXBTC","LINKBTC","QNTBTC","ALGOBTC","BNBBTC","LTCBTC","XLMBTC","ETCBTC","NEARBTC","IMXBTC","XRPBTC","UNIBTC","FILBTC","AXSBTC","DOTBTC","ICPBTC","OPBTC","EGLDBTC","HBARBTC"],
    bnb: ["DOGEBNB","BCHBNB","ATOMBNB","ADABNB","SOLBNB","AVAXBNB","VETBNB","TRXBNB","LINKBNB","LTCBNB","ETCBNB","XRPBNB","FILBNB","DOTBNB","AAVEBNB"]
};

const ASSETS_CONFIG = {
    stocks: [
        // { symbol: "GAZPRUB", api: "GAZP" }, { symbol: "ALRSRUB", api: "ALRS" },
        // { symbol: "WUSHRUB", api: "WUSH" }, { symbol: "LKOHRUB", api: "LKOH" },
        // { symbol: "MOEXRUB", api: "MOEX" }, { symbol: "MTSSRUB", api: "MTSS" },
        // { symbol: "BELURUB", api: "BELU" }, { symbol: "SBERRUB", api: "SBER" },
        // { symbol: "SBERPRUB", api: "SBERP" }, { symbol: "TATNRUB", api: "TATN" },
        // { symbol: "TRNFPRUB", api: "TRNFP" }, { symbol: "YDEXRUB", api: "YDEX" },
        // { symbol: "HYDRRUB", api: "HYDR" }
    ],
    resources: [
        // { symbol: "GOLDUSD", api: "GOLD" }, { symbol: "SILVERUSD", api: "SILVER" },
        // { symbol: "COPPERUSD", api: "COPPER" }
    ],
    neft: [
        // { symbol: "BRENTUSD", api: "BRENT" }, { symbol: "WTIUSD", api: "WTI" }
    ]
};
// Функция для красивого отображения цены без экспонент (1e-7)
const formatPrice = (value) => {
    const num = Number(value);
    if (isNaN(num)) return "0.00";
    if (num === 0) return "0.00";

    // Если цена очень маленькая (меньше 1$), нужно больше знаков
    if (num < 1) {
        // toFixed(8) превратит 1e-6 в "0.00000100"
        // replace(/\.?0+$/, "") уберет лишние нули в конце ("0.00000100" -> "0.000001")
        return num.toFixed(8).replace(/\.?0+$/, "");
    }
    
    // Для недорогих монет (1$ - 50$) показываем 4 знака (например, DOT или LINK)
    if (num < 50) {
        return num.toFixed(4).replace(/\.?0+$/, "");
    }

    // Для дорогих монет (BTC, ETH, Stocks) оставляем классические 2 знака
    return num.toFixed(2);
};
const formatDecimal = (value, exp = -2) => {
    if (isNaN(value)) return "0.00";
    const decimalAdjust = (type, value, exp) => {
        if (typeof exp === 'undefined' || +exp === 0) return Math[type](value);
        value = +value;
        exp = +exp;
        if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) return NaN;
        value = value.toString().split('e');
        value = Math[type](+(value[0] + 'e' + (value[1] ? (+value[1] - exp) : -exp)));
        value = value.toString().split('e');
        return +(value[0] + 'e' + (value[1] ? (+value[1] + exp) : exp));
    };
    return decimalAdjust('floor', value, exp);
};

const Market = ({ stateActive, setStateActive }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    
    const [activeCoin, setActiveCoin] = useState(() => localStorage.getItem("activeCoin") || "usdt");
    const [marketData, setMarketData] = useState({
        usdt: [], btc: [], bnb: [], stocks: [], resources: [], neft: []
    });
    // Начинаем с false, но загрузка пойдет сразу при маунте
    const [isLoaded, setIsLoaded] = useState(false);
    const [query, setQuery] = useState("");
    
    const wsRef = useRef(null);
    const activeRef = useRef(stateActive); // Ref для хранения актуального stateActive внутри таймеров

    // Обновляем ref при изменении пропса
    useEffect(() => {
        activeRef.current = stateActive;
    }, [stateActive]);

    // --- 1. ПАКЕТНАЯ ЗАГРУЗКА ДАННЫХ (1 сетевой запрос вместо 83) ---
    const loadInitialData = useCallback(async (signal) => {
        try {
            const allSymbols = [
                ...CRYPTO_SYMBOLS.usdt,
                ...CRYPTO_SYMBOLS.btc,
                ...CRYPTO_SYMBOLS.bnb
            ];
            const encoded = encodeURIComponent(JSON.stringify(allSymbols));
            const res = await axios.get(`${NEW_API_BASE}/api/24hr?symbols=${encoded}`, { signal });

            if (!signal.aborted && Array.isArray(res.data)) {
                const map = new Map(res.data.map(item => [item.symbol, {
                    symbol: item.symbol,
                    lastPrice: item.lastPrice,
                    priceChangePercent: item.priceChangePercent
                }]));

                setMarketData(prev => ({
                    ...prev,
                    usdt: CRYPTO_SYMBOLS.usdt.map(s => map.get(s)).filter(Boolean),
                    btc: CRYPTO_SYMBOLS.btc.map(s => map.get(s)).filter(Boolean),
                    bnb: CRYPTO_SYMBOLS.bnb.map(s => map.get(s)).filter(Boolean)
                }));
                setIsLoaded(true);
            }
        } catch (error) {
            if (!signal?.aborted) {
                setIsLoaded(true);
            }
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        loadInitialData(controller.signal);
        return () => controller.abort();
    }, [loadInitialData]);

    // --- 2. WEBSOCKET (Подключается один раз, фильтрует лишние ре-рендеры) ---
    useEffect(() => {
        let ws = null;
        let isStopped = false;
        let reconnectTimer = null;

        const connect = () => {
            if (isStopped) return;
            ws = new WebSocket(WS_URL);
            wsRef.current = ws;

            ws.onmessage = (event) => {
                // Если окно закрыто, не парсим и не тратим ресурсы
                if (!activeRef.current) return;

                try {
                    const data = JSON.parse(event.data);
                    if (!data?.symbol || data.price == null) return;

                    const sym = data.symbol;
                    const updateCategory = (list) => {
                        const idx = list.findIndex(item => item.symbol === sym);
                        if (idx === -1 || list[idx].lastPrice === data.price) return list;
                        const next = [...list];
                        next[idx] = { ...next[idx], lastPrice: data.price };
                        return next;
                    };

                    setMarketData(prev => {
                        if (sym.endsWith("USDT")) {
                            const nextUsdt = updateCategory(prev.usdt);
                            return nextUsdt === prev.usdt ? prev : { ...prev, usdt: nextUsdt };
                        } else if (sym.endsWith("BTC")) {
                            const nextBtc = updateCategory(prev.btc);
                            return nextBtc === prev.btc ? prev : { ...prev, btc: nextBtc };
                        } else if (sym.endsWith("BNB")) {
                            const nextBnb = updateCategory(prev.bnb);
                            return nextBnb === prev.bnb ? prev : { ...prev, bnb: nextBnb };
                        }
                        return prev;
                    });
                } catch (e) {}
            };

            ws.onclose = () => {
                if (!isStopped) {
                    reconnectTimer = setTimeout(connect, 3000);
                }
            };

            ws.onerror = () => {
                if (ws) ws.close();
            };
        };

        connect();

        return () => {
            isStopped = true;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            if (ws) ws.close();
        };
    }, []);


    // --- RENDER ---
    
    // ... (весь код рендера и UI хелперов остался тем же)
    const handleCategoryChange = (category) => {
        setActiveCoin(category);
        localStorage.setItem("activeCoin", category);
        const navConfig = {
            stocks: { path: "/market-stocks", param: "symbol=GAZPRUB" },
            resources: { path: "/market-resources", param: "resource=GOLDUSD" },
            neft: { path: "/market-resources", param: "resource=BRENTUSD" },
            usdt: { path: "/market", param: "symbol=BTCUSDT" },
            btc: { path: "/market", param: "symbol=ETHBTC" },
            bnb: { path: "/market", param: "symbol=BNBUSDT" }
        };
        const target = navConfig[category];
        if (target) navigate(`${target.path}?${target.param}`);
    };

    const handleItemClick = (symbol, category) => {
        let path = "/market";
        let paramKey = "symbol";
        if (category === 'stocks') path = "/market-stocks";
        if (['resources', 'neft'].includes(category)) {
            path = "/market-resources";
            paramKey = "resource";
        }
        navigate(`${path}?${paramKey}=${symbol}`);
        document.location.reload()
    };

    const renderListItem = (item, baseCurrency) => {
        const displaySymbol = item.symbol.replace(baseCurrency, '');
        const priceChange = formatDecimal(item.priceChangePercent);
        const isNegative = String(priceChange).startsWith('-');
        
        return (
            <li key={item.symbol} onClick={() => handleItemClick(item.symbol, activeCoin)}>
                <div className="currency">
                    <h4>{displaySymbol}<span>/{baseCurrency}</span></h4>
                </div>
                <p className='lastPrice'>{formatPrice(item.lastPrice)}</p>
                <p className={isNegative ? 'precent minus' : 'precent'}>
                    {priceChange} %
                </p>
            </li>
        );
    };

    let currentList = marketData[activeCoin] || [];
    let baseDisplayCurrency = "";
    if (activeCoin === 'usdt') baseDisplayCurrency = "USDT";
    else if (activeCoin === 'btc') baseDisplayCurrency = "BTC";
    else if (activeCoin === 'bnb') baseDisplayCurrency = "BNB";
    else if (activeCoin === 'stocks') baseDisplayCurrency = "RUB";
    else if (['resources', 'neft'].includes(activeCoin)) baseDisplayCurrency = "USD";

    const filteredList = currentList.filter(item => 
        !query || item.symbol.toLowerCase().includes(query.toLowerCase())
    );

    return (
        <div className={`Market ${stateActive ? 'MarketActive' : ''}`}>
            <div className="headline">
                <h3>{t("market.markets")}</h3>
                <MarketClose onClick={() => setStateActive(false)} />
            </div>
            <div className="searchCoin">
                <div className="searchIco"><MarketSearch /></div>
                <input 
                    type="text" 
                    onChange={e => setQuery(e.target.value)} 
                    placeholder={t("market.searchCoin")} 
                    value={query}
                />
            </div>

            <div className="menu">
                {['usdt', 'btc', 'bnb'].map(coin => (
                    <p key={coin} onClick={() => handleCategoryChange(coin)} className={activeCoin === coin ? "active" : ""}>
                        {coin.toUpperCase()}
                    </p>
                ))}
                {/* <p onClick={() => handleCategoryChange("stocks")} className={activeCoin === "stocks" ? "active" : ""}>{t("market.stocks")}</p>
                <p onClick={() => handleCategoryChange("neft")} className={activeCoin === "neft" ? "active" : ""}>{t("market.oil")}</p> */}
            </div>

            <div className="sampleBlock">
                <div className="spot">
                    <ul>
                        <li style={{ width: 110 }}>{t("market.pair")}</li>
                        <li>{t("market.lastPrice")}</li>
                        <li>{t("market.changePrice")}</li>
                    </ul>
                    <div className="list scroll-pairs">
                        {isLoaded ? (
                            <ul>
                                {filteredList.length > 0 ? (
                                    filteredList.map(item => renderListItem(item, baseDisplayCurrency))
                                ) : (
                                    <li className="no-data">Нет данных</li>
                                )}
                            </ul>
                        ) : (
                            <div className="loading">{t("market.loading")}</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Market; 
